import React from 'react';

import {ArrowRotateRight} from '@gravity-ui/icons';
import type {ButtonButtonProps} from '@gravity-ui/uikit';
import {Button, Text, useMobile} from '@gravity-ui/uikit';

import {useMobileControlSize} from '../../../hooks/useMobileControlSize';
import type {SuggestionClickHandler, SuggestionsItem} from '../../../types/common';
import {block} from '../../../utils/cn';
import {Suggestions} from '../../molecules/Suggestions';

import {i18n} from './i18n';

import './EmptyContainer.scss';

const b = block('empty-container');

/**
 * Suggestion item for EmptyContainer
 */
export type Suggestion = SuggestionsItem;

/**
 * Alignment options for content elements
 */
export type ContentAlignment = 'left' | 'center' | 'right';

/**
 * Alignment configuration for different sections
 */
export interface AlignmentConfig {
    /** Alignment for the image */
    image?: ContentAlignment;
    /** Alignment for custom hero content; falls back to image alignment. */
    hero?: ContentAlignment;
    /** Alignment for the title */
    title?: ContentAlignment;
    /** Alignment for the description */
    description?: ContentAlignment;
}

/**
 * Props for the EmptyContainer component
 */
export interface EmptyContainerProps {
    /** Image or icon to display at the top */
    image?: React.ReactNode;
    /** Custom hero content that replaces image when provided. */
    heroContent?: React.ReactNode;
    /** Title text or custom React element for the welcome screen */
    title?: React.ReactNode;
    /** Description text or custom React element explaining the functionality */
    description?: React.ReactNode;
    /** Title for the suggestions section - can be string or custom React element */
    suggestionTitle?: React.ReactNode;
    /** Array of suggestion items */
    suggestions?: Suggestion[];
    /** Callback when a suggestion is clicked */
    onSuggestionClick?: SuggestionClickHandler;
    /** Alignment configuration for image, title, and description */
    alignment?: AlignmentConfig;
    /** Layout orientation for suggestions: 'grid' for horizontal, 'list' for vertical */
    layout?: 'grid' | 'list';
    /** Enable text wrapping inside suggestion buttons instead of ellipsis */
    wrapText?: boolean;
    /** Size of suggestion buttons. Defaults to `m`, or `xl` in mobile mode */
    suggestionsSize?: ButtonButtonProps['size'];
    /** Callback for showing more suggestions */
    showMore?: () => void;
    /** Size of the show more button. Defaults to `l`, or `xl` in mobile mode */
    showMoreSize?: ButtonButtonProps['size'];
    /** Custom text for the show more button */
    showMoreText?: string;
    /** Additional CSS class */
    className?: string;
    /** QA/test identifier */
    qa?: string;
}

/**
 * EmptyContainer component - displays a welcome screen with image, title, description,
 * and suggestions
 *
 * @param props - Component props
 * @returns React component
 */
export function EmptyContainer(props: EmptyContainerProps) {
    const {
        image,
        heroContent,
        title,
        description,
        suggestionTitle,
        suggestions = [],
        onSuggestionClick,
        alignment,
        layout,
        wrapText,
        suggestionsSize,
        showMore,
        showMoreSize: showMoreSizeProp,
        showMoreText,
        className,
        qa,
    } = props;

    const isMobile = useMobile();
    const resolvedLayout = layout ?? (isMobile ? 'list' : 'grid');
    const showMoreSize = useMobileControlSize(showMoreSizeProp, 'l', 'xl');

    const hasContent = [
        heroContent !== undefined,
        image,
        title,
        description,
        suggestions.length,
    ].some(Boolean);

    // Define alignment for each element
    const imageAlignment = alignment?.image || 'left';
    const heroAlignment = alignment?.hero || imageAlignment;
    const titleAlignment = alignment?.title || 'left';
    const descriptionAlignment = alignment?.description || 'left';

    // Define text for "Show more" button with localization support
    const showMoreButtonText = showMoreText || i18n('show-more-button');
    let heroOrImage: React.ReactNode;
    if (heroContent !== undefined) {
        heroOrImage = (
            <div className={b('hero-container', {align: heroAlignment})}>{heroContent}</div>
        );
    } else if (image) {
        heroOrImage = <div className={b('image-container', {align: imageAlignment})}>{image}</div>;
    }

    return (
        <div className={b({mobile: isMobile}, className)} data-qa={qa}>
            <div className={b('content')}>
                {hasContent && (
                    <>
                        <div className={b('welcome-section')}>
                            {heroOrImage}

                            <div className={b('text-container')}>
                                {title && (
                                    <Text
                                        variant="header-2"
                                        className={b('title', {align: titleAlignment})}
                                    >
                                        {title}
                                    </Text>
                                )}

                                {description && (
                                    <Text
                                        variant="body-2"
                                        color="complementary"
                                        className={b('description', {align: descriptionAlignment})}
                                    >
                                        {description}
                                    </Text>
                                )}
                            </div>
                        </div>

                        {suggestions && suggestions.length > 0 && onSuggestionClick && (
                            <div className={b('suggestions-section')}>
                                {suggestionTitle && (
                                    <div className={b('suggestions-title')}>
                                        {typeof suggestionTitle === 'string' ? (
                                            <Text variant="subheader-3" color="primary">
                                                {suggestionTitle}
                                            </Text>
                                        ) : (
                                            suggestionTitle
                                        )}
                                    </div>
                                )}
                                <div className={b('suggestions-list')}>
                                    <Suggestions
                                        items={suggestions}
                                        onClick={onSuggestionClick}
                                        layout={resolvedLayout}
                                        wrapText={wrapText}
                                        size={suggestionsSize}
                                    />
                                </div>
                                {showMore && (
                                    <div className={b('show-more')}>
                                        <Button
                                            view="flat-secondary"
                                            size={showMoreSize}
                                            onClick={showMore}
                                            className={b('show-more-button')}
                                        >
                                            <Button.Icon>
                                                <ArrowRotateRight />
                                            </Button.Icon>
                                            {showMoreButtonText}
                                        </Button>
                                    </div>
                                )}
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
}
